import React, { useEffect, useMemo, useState } from "react";
import { EDocumentDetail, EDocumentListItem, Firm } from "../types";
import {
  eDocumentService,
  eDocumentActions,
  firmService,
} from "../services/dataService";
import { formatCurrency, turkishIncludes } from "../utils/formatters";
import { flattenHistory, HistoryEvent } from "../utils/documentHistory";
import PageLoader from "../components/PageLoader";
import FirmPickerField from "../components/FirmPicker";
import SendDocumentModal from "../components/SendDocumentModal";
import { usePermissions } from "../context/PermissionContext";
import "./Documents.css";

/**
 * Kesilen Belgeler — firmalara kesilen tüm e-belgelerin listesi ve detayı.
 * Siparişe bağlı olmayan satışlar (numune, hizmet, kumaş/hurda satışı…) için
 * "Yeni Fatura" ile sipariş dışı belge de buradan kesilir.
 *
 * Liste YEREL kayıttan gelir (hızlı; başarısız denemeler dahil; servisler
 * çökse de çalışır). Canlı katman — güncel durum, HTML/PDF çıktısı — detay
 * açıldığında servislerden çekilir ve yerel kayda işlenir. Çıktılar backend
 * üzerinden proxy'lenir; platform token'ı tarayıcıya inmez.
 */

/* ---------- yardımcılar ---------- */

const errorText = (error: any, fallback: string): string => {
  const d = error?.response?.data;
  if (!d) return fallback;
  if (d.detail && d.title) return `${d.title}: ${d.detail}`;
  return d.detail || d.title || fallback;
};

type Tone = "ok" | "info" | "warn" | "err" | "muted";

const stateLabel = (d: EDocumentListItem): { text: string; tone: Tone } => {
  if (!d.success) {
    return { text: d.errorCode === "NETWORK" ? "Ulaşılamadı" : "Hata", tone: "err" };
  }
  switch ((d.remoteState || "").toLowerCase()) {
    case "gibaccepted":
      return { text: "GİB Kabul", tone: "ok" };
    case "senttoprovider":
      return { text: "Entegratörde", tone: "info" };
    case "queued":
      return { text: "Kuyrukta", tone: "info" };
    case "rejected":
    case "gibrejected":
      return { text: "Reddedildi", tone: "err" };
    case "cancelled":
      return { text: "İptal", tone: "warn" };
    case "draft":
      return { text: "Taslak", tone: "muted" };
    case "sent":
      return { text: "Gönderildi", tone: "ok" };
    case "accepted":
      return { text: "Kabul Edildi", tone: "ok" };
    default:
      return { text: d.remoteState || "Kesildi", tone: "ok" };
  }
};

const serviceName = (kind: string) => (kind === "EDM" ? "e-Fatura" : "Faturasız");

/** Yerel takvim günü (toISOString UTC'ye çevirip TR'de bir gün geri kaydırır). */
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtDate = (s: string) => new Date(s).toLocaleDateString("tr-TR");
const fmtTime = (s: string) =>
  new Date(s).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
const fmtDateTime = (s: string) => new Date(s).toLocaleString("tr-TR");

/* ---------- ikonlar ---------- */

const I = {
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
  ),
  refresh: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>
  ),
  file: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg>
  ),
  download: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v11" /><path d="m7 10 5 5 5-5" /><path d="M4 20h16" /></svg>
  ),
  external: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" /></svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 4 4L19 6" /></svg>
  ),
  undo: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 14 4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" /></svg>
  ),
  history: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20L12 3Z" /><path d="M12 10v4" /><path d="M12 17h.01" /></svg>
  ),
  inbox: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>
  ),
  filterOff: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
  ),
};

/* ---------- küçük bileşenler ---------- */

const Badge: React.FC<{ tone: Tone | "efatura" | "proforma"; title?: string; children: React.ReactNode }> = ({
  tone,
  title,
  children,
}) => (
  <span className={`ed-badge ${tone}`} title={title}>
    {children}
  </span>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="ed-field">
    <span>
      {label}
      {hint && <em>{hint}</em>}
    </span>
    {children}
  </label>
);

/* ---------- sayfa ---------- */

type DatePreset = "7" | "30" | "month" | "year" | "all";

const Documents: React.FC = () => {
  const { has } = usePermissions();
  const canSend = has("e-documents:send");
  const canManage = has("e-documents:manage");

  const [items, setItems] = useState<EDocumentListItem[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [initial, setInitial] = useState(true);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  // Süzgeçler
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState<"" | "EDM" | "FATURASIZ">("");
  const [statusFilter, setStatusFilter] = useState<"" | "success" | "failed">("");
  const [firmFilter, setFirmFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Detay çekmecesi
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<EDocumentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEvent[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Formlar
  const [emailForm, setEmailForm] = useState({ open: false, to: "", note: "" });
  const [cancelForm, setCancelForm] = useState({ open: false, reason: "" });
  const [objectionForm, setObjectionForm] = useState({
    open: false,
    documentNo: "",
    documentDate: iso(new Date()),
    method: "KEP",
    description: "",
  });

  /* ---- yükleme ---- */

  const load = async () => {
    setLoading(true);
    setNotice(null);
    try {
      const [docs, firmList] = await Promise.all([
        eDocumentService.list({ from: dateFrom || undefined, to: dateTo || undefined }),
        firms.length === 0 ? firmService.getFirms() : Promise.resolve(firms),
      ]);
      setItems(docs);
      if (firms.length === 0) setFirms(firmList);
    } catch {
      setNotice("Belge listesi yüklenemedi.");
    } finally {
      setLoading(false);
      setInitial(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  /* ---- süzme / özet ---- */

  const filtered = useMemo(
    () =>
      items.filter((d) => {
        if (serviceFilter && d.serviceKind !== serviceFilter) return false;
        if (statusFilter === "success" && !d.success) return false;
        if (statusFilter === "failed" && d.success) return false;
        if (firmFilter && d.firmId !== firmFilter) return false;
        if (search) {
          const hit =
            turkishIncludes(d.documentNumber || "", search) ||
            turkishIncludes(d.firmName || "", search) ||
            turkishIncludes(d.modelCode || "", search) ||
            turkishIncludes(d.summary || "", search) ||
            turkishIncludes(d.sentBy || "", search);
          if (!hit) return false;
        }
        return true;
      }),
    [items, serviceFilter, statusFilter, firmFilter, search],
  );

  const summary = useMemo(() => {
    const ok = filtered.filter((d) => d.success);
    const failed = filtered.length - ok.length;
    const efatura = ok.filter((d) => d.serviceKind === "EDM").length;
    const proforma = ok.length - efatura;
    const totals: Record<string, number> = {};
    for (const d of ok) {
      if (d.grandTotal == null) continue;
      const cur = d.currencyCode || "TRY";
      totals[cur] = (totals[cur] || 0) + d.grandTotal;
    }
    const currencies = Object.keys(totals).sort((a, b) => (a === "TRY" ? -1 : b === "TRY" ? 1 : a.localeCompare(b)));
    return { total: filtered.length, ok: ok.length, failed, efatura, proforma, totals, currencies };
  }, [filtered]);

  const sortedFirms = useMemo(
    () => firms.slice().sort((a, b) => a.firmName.localeCompare(b.firmName, "tr")),
    [firms],
  );

  const hasFilter = !!(search || serviceFilter || statusFilter || firmFilter || dateFrom || dateTo);

  const clearFilters = () => {
    setSearch("");
    setServiceFilter("");
    setStatusFilter("");
    setFirmFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const activePreset = (): DatePreset | null => {
    const today = new Date();
    if (!dateFrom && !dateTo) return "all";
    if (dateTo !== iso(today)) return null;
    const from = new Date(dateFrom);
    const diff = Math.round((today.getTime() - from.getTime()) / 86400000);
    if (diff === 6) return "7";
    if (diff === 29) return "30";
    if (dateFrom === iso(new Date(today.getFullYear(), today.getMonth(), 1))) return "month";
    if (dateFrom === iso(new Date(today.getFullYear(), 0, 1))) return "year";
    return null;
  };

  const applyPreset = (p: DatePreset) => {
    const today = new Date();
    if (p === "all") {
      setDateFrom("");
      setDateTo("");
      return;
    }
    let from: Date;
    if (p === "7") from = new Date(today.getTime() - 6 * 86400000);
    else if (p === "30") from = new Date(today.getTime() - 29 * 86400000);
    else if (p === "month") from = new Date(today.getFullYear(), today.getMonth(), 1);
    else from = new Date(today.getFullYear(), 0, 1);
    setDateFrom(iso(from));
    setDateTo(iso(today));
  };

  /* ---- detay ---- */

  const patchRow = (d: EDocumentDetail) =>
    setItems((prev) =>
      prev.map((x) =>
        x.orderInvoiceId === d.orderInvoiceId
          ? {
              ...x,
              remoteState: d.remoteState ?? x.remoteState,
              gibStatusCode: d.gibStatusCode ?? x.gibStatusCode,
              viewUrl: d.viewUrl ?? x.viewUrl,
              grandTotal: d.grandTotal ?? x.grandTotal,
            }
          : x,
      ),
    );

  const reloadDetail = async (id: string) => {
    const d = await eDocumentService.detail(id);
    setDetail(d);
    patchRow(d);
  };

  const openDetail = async (id: string) => {
    setDetailId(id);
    setDetailLoading(true);
    setDetail(null);
    setHistory(null);
    setActionMsg(null);
    setEmailForm({ open: false, to: "", note: "" });
    setCancelForm({ open: false, reason: "" });
    setObjectionForm((p) => ({ ...p, open: false, documentNo: "", description: "" }));
    try {
      const d = await eDocumentService.detail(id);
      setDetail(d);
      patchRow(d);
    } catch {
      setNotice("Belge detayı yüklenemedi.");
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  useEffect(() => {
    if (!detailId) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeDetail();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId]);

  /** İşlem çalıştırır, sonucu çekmecede gösterir ve durumu tazeler. */
  const runAction = async (id: string, label: string, fn: () => Promise<any>, onDone?: () => void) => {
    setBusyId(id);
    setActionMsg(null);
    try {
      await fn();
      setActionMsg({ ok: true, text: `${label} tamamlandı.` });
      onDone?.();
      await reloadDetail(id);
    } catch (error: any) {
      setActionMsg({ ok: false, text: errorText(error, `${label} başarısız.`) });
    } finally {
      setBusyId(null);
    }
  };

  const handleRender = async (id: string, format: "html" | "pdf" | "xml") => {
    setBusyId(id);
    setNotice(null);
    try {
      await eDocumentService.openRender(id, format);
    } catch (error: any) {
      // Blob isteklerinde hata gövdesi de blob gelir; metne çevir.
      let message = "Çıktı alınamadı.";
      try {
        const blob = error?.response?.data;
        if (blob instanceof Blob) {
          const parsed = JSON.parse(await blob.text());
          message = parsed.detail || parsed.title || message;
        }
      } catch {
        /* varsayılan mesaj kalır */
      }
      if (detailId) setActionMsg({ ok: false, text: message });
      else setNotice(message);
    } finally {
      setBusyId(null);
    }
  };

  const fetchHistory = async (id: string) => {
    setHistoryLoading(true);
    try {
      // Platform { states, actions, emails } döner; tek zaman çizelgesine çevrilir.
      setHistory(flattenHistory(await eDocumentActions.history(id)));
    } catch (error: any) {
      setActionMsg({ ok: false, text: errorText(error, "Geçmiş alınamadı.") });
    } finally {
      setHistoryLoading(false);
    }
  };

  if (initial) return <PageLoader />;

  const preset = activePreset();
  const busy = (id: string) => busyId === id;

  /* ---------- görünüm ---------- */

  return (
    <div className="ed-page">
      {/* Başlık */}
      <header className="ed-head">
        <div>
          <span className="ed-kicker">Faturalama</span>
          <h1>Kesilen Belgeler</h1>
          <p>
            Firmalara kesilen e-Fatura / e-Arşiv ve Faturasız belgeler — siparişten ya da sipariş dışı.
            Liste yerel kayıttan gelir; güncel durum ve çıktılar belge açıldığında servisten alınır.
          </p>
        </div>
        <div className="ed-head-actions">
          <button className="ed-btn" onClick={load} disabled={loading}>
            <span className={loading ? "ed-spin-icon" : ""}>{I.refresh}</span>
            {loading ? "Yenileniyor" : "Listeyi yenile"}
          </button>
          {canSend && (
            <button className="ed-btn primary" onClick={() => setComposing(true)} title="Siparişe bağlı olmayan satış için fatura kes">
              {I.plus} Yeni Fatura
            </button>
          )}
        </div>
      </header>

      {/* Özet */}
      <section className="ed-kpis">
        <div className="ed-kpi">
          <span className="l">Belge</span>
          <span className="v">{summary.total}</span>
          <span className="s">{hasFilter ? "süzgeçli sonuç" : "tüm kayıtlar"}</span>
        </div>
        <div className="ed-kpi ok">
          <span className="l">Başarılı</span>
          <span className="v">{summary.ok}</span>
          <span className="s">
            {summary.efatura} e-Fatura · {summary.proforma} Faturasız
          </span>
        </div>
        <div className={`ed-kpi ${summary.failed ? "err" : ""}`}>
          <span className="l">Başarısız deneme</span>
          <span className="v">{summary.failed}</span>
          <span className="s">{summary.failed ? "detayda hata sebebi görünür" : "sorun yok"}</span>
        </div>
        <div className="ed-kpi navy">
          <span className="l">Toplam tutar</span>
          <span className="v">
            {summary.currencies.length
              ? formatCurrency(summary.totals[summary.currencies[0]], summary.currencies[0])
              : "—"}
          </span>
          <span className="s">
            {summary.currencies.length > 1
              ? summary.currencies
                  .slice(1)
                  .map((c) => formatCurrency(summary.totals[c], c))
                  .join(" · ")
              : "başarılı belgeler"}
          </span>
        </div>
      </section>

      {/* Araç çubuğu */}
      <section className="ed-toolbar">
        <div className="ed-toolbar-row">
          <div className="ed-search">
            {I.search}
            <input
              placeholder="Belge no, firma, model veya kesen kişi ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="ed-ib" aria-label="Temizle" onClick={() => setSearch("")}>
                {I.close}
              </button>
            )}
          </div>

          <FirmPickerField variant="toolbar" value={firmFilter} firms={sortedFirms} nullLabel="Tüm firmalar" allowCreate={false} title="Firmaya göre süz" onChange={(f) => setFirmFilter(f?.firmId || "")} />

          <div className="ed-dates">
            <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
            <span>–</span>
            <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>

        <div className="ed-toolbar-row chips">
          <div className="ed-chipgroup" role="group" aria-label="Servis">
            {(
              [
                ["", "Tüm servisler"],
                ["EDM", "e-Fatura"],
                ["FATURASIZ", "Faturasız"],
              ] as const
            ).map(([v, l]) => (
              <button key={v} className={serviceFilter === v ? "on" : ""} onClick={() => setServiceFilter(v)}>
                {l}
              </button>
            ))}
          </div>
          <div className="ed-chipgroup" role="group" aria-label="Durum">
            {(
              [
                ["", "Tüm durumlar"],
                ["success", "Başarılı"],
                ["failed", "Başarısız"],
              ] as const
            ).map(([v, l]) => (
              <button key={v} className={statusFilter === v ? "on" : ""} onClick={() => setStatusFilter(v)}>
                {l}
              </button>
            ))}
          </div>
          <div className="ed-chipgroup" role="group" aria-label="Dönem">
            {(
              [
                ["7", "Son 7 gün"],
                ["30", "Son 30 gün"],
                ["month", "Bu ay"],
                ["year", "Bu yıl"],
                ["all", "Tümü"],
              ] as const
            ).map(([v, l]) => (
              <button key={v} className={preset === v ? "on" : ""} onClick={() => applyPreset(v)}>
                {l}
              </button>
            ))}
          </div>
          {hasFilter && (
            <button className="ed-clear" onClick={clearFilters}>
              {I.filterOff} Süzgeçleri temizle
            </button>
          )}
        </div>
      </section>

      {notice && (
        <div className="ed-notice err">
          {I.alert}
          <span>{notice}</span>
        </div>
      )}

      {/* Liste */}
      <section className={`ed-card ${loading ? "dim" : ""}`}>
        <div className="ed-card-head">
          <span>
            <b>{filtered.length}</b> belge
            {filtered.length !== items.length && <em> / {items.length}</em>}
          </span>
          <span className="ed-hint">Satıra tıklayınca detay açılır</span>
        </div>

        {filtered.length === 0 ? (
          <div className="ed-blank">
            <div className="ed-blank-icon">{I.inbox}</div>
            <b>{items.length === 0 ? "Bu dönemde kesilmiş belge yok" : "Süzgeçle eşleşen belge yok"}</b>
            {items.length === 0
              ? "Fatura Yönetimi'nden ya da \"Yeni Fatura\" ile kesilen belgeler burada listelenir."
              : "Arama ya da süzgeçleri gevşetmeyi deneyin."}
            {hasFilter && (
              <button className="ed-btn" onClick={clearFilters}>
                Süzgeçleri temizle
              </button>
            )}
          </div>
        ) : (
          <div className="ed-table-wrap">
            <table className="ed-table">
              <thead>
                <tr>
                  <th>Belge</th>
                  <th>Firma</th>
                  <th className="hide-md">Model / Kalem</th>
                  <th>Servis</th>
                  <th>Durum</th>
                  <th className="num">Tutar</th>
                  <th className="num hide-md">KDV</th>
                  <th className="hide-sm">Tarih</th>
                  <th className="hide-md">Kesen</th>
                  <th className="act" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const st = stateLabel(d);
                  const canRender = d.success && d.hasDocumentId;
                  return (
                    <tr
                      key={d.orderInvoiceId}
                      className={`${d.success ? "" : "failed"} ${detailId === d.orderInvoiceId ? "on" : ""}`}
                      onClick={() => openDetail(d.orderInvoiceId)}
                    >
                      <td>
                        <div className="ed-docno">
                          <b>{d.documentNumber || "—"}</b>
                          {d.isSimulated && (
                            <Badge tone="warn" title="Simülasyon modunda kesildi">
                              SİM
                            </Badge>
                          )}
                        </div>
                        {!d.success && d.errorMessage && (
                          <div className="ed-sub err" title={d.errorMessage}>
                            {d.errorMessage}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="ed-firm">{d.firmName || "—"}</div>
                        {d.firmCode && <div className="ed-sub">{d.firmCode}</div>}
                      </td>
                      <td className="hide-md">
                        {d.isStandalone ? (
                          <>
                            <Badge tone="muted" title="Siparişe bağlı olmadan kesildi">Sipariş dışı</Badge>
                            {d.summary && <div className="ed-sub" title={d.summary}>{d.summary}</div>}
                          </>
                        ) : (
                          d.modelCode || "—"
                        )}
                      </td>
                      <td>
                        <Badge tone={d.serviceKind === "EDM" ? "efatura" : "proforma"}>{serviceName(d.serviceKind)}</Badge>
                      </td>
                      <td>
                        <Badge tone={st.tone} title={d.errorMessage || undefined}>
                          {st.text}
                        </Badge>
                      </td>
                      <td className="num amount">
                        {d.grandTotal != null ? formatCurrency(d.grandTotal, d.currencyCode || "TRY") : "—"}
                      </td>
                      <td className="num hide-md">{d.vatRate != null ? `%${d.vatRate}` : "Karışık"}</td>
                      <td className="hide-sm">
                        <div>{fmtDate(d.sentAt)}</div>
                        <div className="ed-sub">{fmtTime(d.sentAt)}</div>
                      </td>
                      <td className="hide-md ed-sentby">{d.sentBy || "—"}</td>
                      <td className="act" onClick={(e) => e.stopPropagation()}>
                        <div className="ed-rowacts">
                          <button className="ed-ib" title="Detay" onClick={() => openDetail(d.orderInvoiceId)}>
                            {I.eye}
                          </button>
                          {canRender && (
                            <>
                              <button
                                className="ed-ib"
                                title="HTML görüntüle"
                                disabled={busy(d.orderInvoiceId)}
                                onClick={() => handleRender(d.orderInvoiceId, "html")}
                              >
                                {I.file}
                              </button>
                              <button
                                className="ed-ib"
                                title="PDF indir"
                                disabled={busy(d.orderInvoiceId)}
                                onClick={() => handleRender(d.orderInvoiceId, "pdf")}
                              >
                                {I.download}
                              </button>
                            </>
                          )}
                          {d.viewUrl && (
                            <a className="ed-ib" title="Entegratörde görüntüle" href={d.viewUrl} target="_blank" rel="noreferrer">
                              {I.external}
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Detay çekmecesi */}
      {detailId && (
        <div className="ed-overlay" onClick={closeDetail}>
          <aside className="ed-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {detailLoading || !detail ? (
              <div className="ed-loading">
                <span className="ed-spin" /> Detay yükleniyor
              </div>
            ) : (
              <>
                <div className="ed-drawer-head">
                  <div className="ed-drawer-title">
                    <span className="ed-kicker">{serviceName(detail.serviceKind)} belgesi</span>
                    <h2>{detail.documentNumber || "Başarısız deneme"}</h2>
                    <div className="ed-badges">
                      <Badge tone={stateLabel(detail).tone}>{stateLabel(detail).text}</Badge>
                      {detail.isSimulated && <Badge tone="warn">Simülasyon</Badge>}
                      {!detail.remote.reachable && <Badge tone="muted">Servise ulaşılamadı</Badge>}
                    </div>
                  </div>
                  <button className="ed-ib big" aria-label="Kapat" onClick={closeDetail}>
                    {I.close}
                  </button>
                </div>

                <div className="ed-drawer-body">
                  {!detail.remote.reachable && (
                    <div className="ed-notice warn">
                      {I.alert}
                      <span>{detail.remote.error || "Servise ulaşılamadı; son bilinen durum gösteriliyor."}</span>
                    </div>
                  )}

                  {actionMsg && (
                    <div className={`ed-notice ${actionMsg.ok ? "ok" : "err"}`}>
                      {actionMsg.ok ? I.check : I.alert}
                      <span>{actionMsg.text}</span>
                    </div>
                  )}

                  {/* Kimlik bilgileri */}
                  <dl className="ed-facts">
                    <div>
                      <dt>Firma</dt>
                      <dd>
                        {detail.firm?.firmName || "—"}
                        {detail.firm?.taxNumber && <small>VKN {detail.firm.taxNumber}</small>}
                      </dd>
                    </div>
                    <div>
                      <dt>{detail.isStandalone ? "Kaynak" : "Model"}</dt>
                      <dd>
                        {detail.isStandalone ? "Sipariş dışı" : detail.model ? detail.model.modelCode : "—"}
                        {detail.isStandalone
                          ? detail.lines?.[0]?.name && <small>{detail.lines[0].name}{detail.lines.length > 1 ? ` +${detail.lines.length - 1} kalem` : ""}</small>
                          : detail.model?.modelName && <small>{detail.model.modelName}</small>}
                      </dd>
                    </div>
                    <div>
                      <dt>Genel toplam</dt>
                      <dd className="amount">
                        {detail.grandTotal != null ? formatCurrency(detail.grandTotal, detail.currencyCode || "TRY") : "—"}
                      </dd>
                    </div>
                    <div>
                      <dt>Kesen</dt>
                      <dd>
                        {detail.sentBy || "—"}
                        <small>{fmtDateTime(detail.sentAt)}</small>
                      </dd>
                    </div>
                    {detail.serviceKind === "EDM" && (
                      <div>
                        <dt>GİB durumu</dt>
                        <dd>{detail.remote.gibStatusDescription || detail.gibStatusCode || "—"}</dd>
                      </div>
                    )}
                    {detail.remote.eArchiveReportStatus && (
                      <div>
                        <dt>e-Arşiv raporu</dt>
                        <dd>{detail.remote.eArchiveReportStatus}</dd>
                      </div>
                    )}
                    {detail.errorMessage && (
                      <div className="wide err">
                        <dt>Hata</dt>
                        <dd>
                          {detail.errorCode && <code>{detail.errorCode}</code>} {detail.errorMessage}
                        </dd>
                      </div>
                    )}
                  </dl>

                  {/* Çıktılar */}
                  {(detail.success || detail.viewUrl) && (
                    <section className="ed-sec">
                      <h3>Çıktılar</h3>
                      <div className="ed-actions">
                        {detail.success && (
                          <>
                            <button className="ed-btn" disabled={busy(detail.orderInvoiceId)} onClick={() => handleRender(detail.orderInvoiceId, "html")}>
                              {I.file} HTML aç
                            </button>
                            <button className="ed-btn" disabled={busy(detail.orderInvoiceId)} onClick={() => handleRender(detail.orderInvoiceId, "pdf")}>
                              {I.download} PDF indir
                            </button>
                            {detail.serviceKind === "EDM" && (
                              <button className="ed-btn" disabled={busy(detail.orderInvoiceId)} onClick={() => handleRender(detail.orderInvoiceId, "xml")}>
                                {I.download} XML indir
                              </button>
                            )}
                          </>
                        )}
                        {detail.viewUrl && (
                          <a className="ed-btn" href={detail.viewUrl} target="_blank" rel="noreferrer">
                            {I.external} Entegratörde görüntüle
                          </a>
                        )}
                      </div>
                    </section>
                  )}

                  {/* Satırlar */}
                  {detail.lines && detail.lines.length > 0 && (
                    <section className="ed-sec">
                      <h3>Belge satırları</h3>
                      <div className="ed-table-wrap tight">
                        <table className="ed-table small">
                          <thead>
                            <tr>
                              <th>İş / ürün</th>
                              <th className="num">Miktar</th>
                              <th className="num">Birim fiyat</th>
                              <th className="num">KDV</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.lines.map((l, i) => (
                              <tr key={i} className="static">
                                <td>
                                  {l.name}
                                  {l.description && <div className="ed-sub">{l.description}</div>}
                                </td>
                                <td className="num">{l.quantity}</td>
                                <td className="num">{formatCurrency(l.unitPrice, detail.currencyCode || "TRY")}</td>
                                <td className="num">%{l.vatRate}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  )}

                  {/* İşlemler */}
                  {detail.success && (canSend || detail.serviceKind === "EDM") && (
                    <section className="ed-sec">
                      <h3>İşlemler</h3>
                      <div className="ed-actions">
                        {canSend && (
                          <button
                            className={`ed-btn ${emailForm.open ? "on" : ""}`}
                            disabled={busy(detail.orderInvoiceId)}
                            onClick={() => setEmailForm({ open: !emailForm.open, to: detail.firm?.email || "", note: "" })}
                          >
                            {I.mail} E-posta gönder
                          </button>
                        )}

                        {/* Yeniden gönderim YENİ NUMARA TÜKETMEZ; yalnızca GİB'e ulaşmamış belgede anlamlı. */}
                        {canSend &&
                          detail.serviceKind === "EDM" &&
                          !["gibaccepted", "delivered", "acceptedbybuyer"].includes((detail.remoteState || "").toLowerCase()) && (
                            <button
                              className="ed-btn"
                              disabled={busy(detail.orderInvoiceId)}
                              onClick={() => runAction(detail.orderInvoiceId, "Yeniden gönderim", () => eDocumentActions.resend(detail.orderInvoiceId))}
                            >
                              {I.refresh} Yeniden gönder
                            </button>
                          )}

                        {canSend && detail.serviceKind === "FATURASIZ" && (
                          <>
                            <button
                              className="ed-btn"
                              disabled={busy(detail.orderInvoiceId)}
                              onClick={() => runAction(detail.orderInvoiceId, "Kabul işaretleme", () => eDocumentActions.changeState(detail.orderInvoiceId, "Accepted"))}
                            >
                              {I.check} Kabul edildi
                            </button>
                            <button
                              className="ed-btn"
                              disabled={busy(detail.orderInvoiceId)}
                              onClick={() => runAction(detail.orderInvoiceId, "Geri çekme", () => eDocumentActions.changeState(detail.orderInvoiceId, "Cancelled"))}
                            >
                              {I.undo} Geri çek
                            </button>
                          </>
                        )}

                        {detail.serviceKind === "EDM" && (
                          <button className="ed-btn" disabled={historyLoading} onClick={() => fetchHistory(detail.orderInvoiceId)}>
                            {I.history} {historyLoading ? "Getiriliyor" : "Servis geçmişi"}
                          </button>
                        )}
                      </div>

                      {emailForm.open && (
                        <div className="ed-form">
                          <Field label="Alıcı e-posta">
                            <input
                              type="email"
                              value={emailForm.to}
                              placeholder="musteri@ornek.com"
                              onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                            />
                          </Field>
                          <Field label="Not" hint="isteğe bağlı, ödeme hatırlatması">
                            <input
                              value={emailForm.note}
                              maxLength={300}
                              placeholder="Ödemenizi 15 gün içinde rica ederiz."
                              onChange={(e) => setEmailForm({ ...emailForm, note: e.target.value })}
                            />
                          </Field>
                          <p className="ed-form-hint">
                            Belge ek olarak gider; Faturasız belgede e-postaya resmi fatura olmadığı notu ayrıca eklenir.
                          </p>
                          <div className="ed-form-foot">
                            <button className="ed-btn" onClick={() => setEmailForm({ open: false, to: "", note: "" })}>
                              Vazgeç
                            </button>
                            <button
                              className="ed-btn primary"
                              disabled={busy(detail.orderInvoiceId) || !emailForm.to.trim()}
                              onClick={() =>
                                runAction(
                                  detail.orderInvoiceId,
                                  "E-posta gönderimi",
                                  () => eDocumentActions.sendEmail(detail.orderInvoiceId, [emailForm.to.trim()], emailForm.note.trim() || undefined),
                                  () => setEmailForm({ open: false, to: "", note: "" }),
                                )
                              }
                            >
                              {busy(detail.orderInvoiceId) ? "Gönderiliyor" : "Gönder"}
                            </button>
                          </div>
                        </div>
                      )}
                    </section>
                  )}

                  {/* Servis geçmişi */}
                  {history && (
                    <section className="ed-sec">
                      <h3>Servis geçmişi</h3>
                      {history.length === 0 ? (
                        <p className="ed-form-hint">Serviste kayıtlı olay yok.</p>
                      ) : (
                        <ol className="ed-timeline">
                          {history.map((h, i) => (
                            <li key={i} className={h.ok === false ? "err" : h.ok ? "ok" : ""}>
                              <i />
                              <div>
                                <div className="ed-tl-top">
                                  <Badge tone={h.ok === false ? "err" : h.kind === "state" ? "info" : "ok"}>{h.title}</Badge>
                                  {h.at && <time>{fmtDateTime(h.at)}</time>}
                                </div>
                                {h.detail && <div className="ed-sub">{h.detail}</div>}
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </section>
                  )}

                  {/* Deneme geçmişi */}
                  {detail.attempts.length > 1 && (
                    <section className="ed-sec">
                      <h3>Deneme geçmişi</h3>
                      <ol className="ed-timeline">
                        {detail.attempts.map((a) => (
                          <li key={a.orderInvoiceId} className={a.success ? "ok" : "err"}>
                            <i />
                            <div>
                              <div className="ed-tl-top">
                                <Badge tone={a.success ? "ok" : "err"}>{a.success ? "Başarılı" : a.errorCode || "Hata"}</Badge>
                                <time>{fmtDateTime(a.sentAt)}</time>
                                <span className="ed-sub">{a.sentBy || "—"}</span>
                              </div>
                              {a.errorMessage && <div className="ed-sub">{a.errorMessage}</div>}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </section>
                  )}

                  {/* Geri alınamaz işlemler */}
                  {detail.success && canManage && (
                    <section className="ed-sec danger">
                      <h3>Geri alınamaz işlemler</h3>
                      <p className="ed-form-hint">
                        {detail.serviceKind === "EDM"
                          ? "GİB'e ulaşmış e-Fatura iptal edilemez; o durumda iade faturası kesilir veya itiraz bildirilir."
                          : "İptal edilen Faturasız belge müşteri bağlantısında geçersiz görünür."}
                      </p>
                      <div className="ed-actions">
                        <button
                          className={`ed-btn danger ${cancelForm.open ? "on" : ""}`}
                          onClick={() => {
                            setCancelForm({ open: !cancelForm.open, reason: "" });
                            setObjectionForm((p) => ({ ...p, open: false }));
                          }}
                        >
                          Belgeyi iptal et
                        </button>
                        {detail.serviceKind === "EDM" && (
                          <button
                            className={`ed-btn danger ${objectionForm.open ? "on" : ""}`}
                            onClick={() => {
                              setObjectionForm((p) => ({ ...p, open: !p.open }));
                              setCancelForm({ open: false, reason: "" });
                            }}
                          >
                            İtiraz bildir
                          </button>
                        )}
                      </div>

                      {cancelForm.open && (
                        <div className="ed-form danger">
                          <Field label="İptal sebebi" hint="zorunlu, en az 5 karakter">
                            <input value={cancelForm.reason} onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })} />
                          </Field>
                          <div className="ed-form-foot">
                            <button className="ed-btn" onClick={() => setCancelForm({ open: false, reason: "" })}>
                              Vazgeç
                            </button>
                            <button
                              className="ed-btn danger solid"
                              disabled={busy(detail.orderInvoiceId) || cancelForm.reason.trim().length < 5}
                              onClick={() =>
                                runAction(
                                  detail.orderInvoiceId,
                                  "İptal",
                                  () => eDocumentActions.cancel(detail.orderInvoiceId, cancelForm.reason.trim()),
                                  () => setCancelForm({ open: false, reason: "" }),
                                )
                              }
                            >
                              İptali onayla
                            </button>
                          </div>
                        </div>
                      )}

                      {objectionForm.open && (
                        <div className="ed-form danger">
                          <p className="ed-form-hint">
                            İtiraz hukuki bir bildirimdir; seçtiğiniz yöntem (KEP, noter, taahhütlü mektup veya GİB) kayda geçer.
                          </p>
                          <div className="ed-grid2">
                            <Field label="İtiraz belge no">
                              <input value={objectionForm.documentNo} onChange={(e) => setObjectionForm({ ...objectionForm, documentNo: e.target.value })} />
                            </Field>
                            <Field label="Belge tarihi">
                              <input type="date" value={objectionForm.documentDate} onChange={(e) => setObjectionForm({ ...objectionForm, documentDate: e.target.value })} />
                            </Field>
                          </div>
                          <Field label="Yöntem">
                            <select value={objectionForm.method} onChange={(e) => setObjectionForm({ ...objectionForm, method: e.target.value })}>
                              <option value="KEP">KEP</option>
                              <option value="NOTER">Noter</option>
                              <option value="TAAHHUTLU_MEKTUP">Taahhütlü mektup</option>
                              <option value="GIB">GİB</option>
                            </select>
                          </Field>
                          <Field label="Açıklama">
                            <input value={objectionForm.description} onChange={(e) => setObjectionForm({ ...objectionForm, description: e.target.value })} />
                          </Field>
                          <div className="ed-form-foot">
                            <button className="ed-btn" onClick={() => setObjectionForm((p) => ({ ...p, open: false }))}>
                              Vazgeç
                            </button>
                            <button
                              className="ed-btn danger solid"
                              disabled={busy(detail.orderInvoiceId) || !objectionForm.documentNo.trim() || !objectionForm.description.trim()}
                              onClick={() =>
                                runAction(
                                  detail.orderInvoiceId,
                                  "İtiraz",
                                  () =>
                                    eDocumentActions.objection(detail.orderInvoiceId, {
                                      documentNo: objectionForm.documentNo.trim(),
                                      documentDate: objectionForm.documentDate,
                                      method: objectionForm.method,
                                      description: objectionForm.description.trim(),
                                    }),
                                  () => setObjectionForm((p) => ({ ...p, open: false })),
                                )
                              }
                            >
                              İtirazı bildir
                            </button>
                          </div>
                        </div>
                      )}
                    </section>
                  )}
                </div>
              </>
            )}
          </aside>
        </div>
      )}

      {/* Sipariş dışı fatura */}
      {composing && (
        <SendDocumentModal
          onClose={() => setComposing(false)}
          onSent={() => load()}
        />
      )}
    </div>
  );
};

export default Documents;
